import React, { useState } from 'react'
import { Container } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import {
  AccountCustomWorkflowRunSummary,
  useLazyGetCustomWorkflowQuery,
  useLazyGetCustomWorkflowRunQuery,
  useListAccountCustomWorkflowRunsQuery,
} from '../../store/customWorkflow/customWorkflows'
import { Modal } from '../../components/Modal/Modal'
import { Spinner } from '../../components/Spinner/Spinner'
import { Alert } from 'react-bootstrap'
import { ProvideInputModal } from '../../components/CustomWorkflows/ProvideInputModal'
import { WorkflowRunStatusBadge } from '../../components/CustomWorkflows/WorkflowRunStatusBadge'
import { RunContextDetail } from '../../components/CustomWorkflows/runContext/RunContextDetail'
import { RunInputSummary } from '../../components/CustomWorkflows/runContext/RunInputSummary'
import { hintsFromSchema, renderWorkflowOutputs } from '../../components/CustomWorkflows/workflowOutputs'
import { WorkflowsTabBar, WorkflowTab } from './Tabs/WorkflowsTabBar'
import { RUN_TABS, RunTabKey } from './Runs/runTabs'
import { clampPages } from './Runs/clampPages'
import { RunsTab } from './Runs/RunsTab'
import { RunRowAction } from './Runs/WorkflowRunRow'
import { RecommendedWorkflows } from './Recommended/RecommendedWorkflows'

const PAGE_SIZE = 10
const TAB_PARAM = 'tab'
const VALID_TABS: WorkflowTab[] = ['needs-action', 'running', 'completed', 'recommended']

export const WorkflowsLanding: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const [searchParams, setSearchParams] = useSearchParams()
  const [pages, setPages] = useState<Record<RunTabKey, number>>({ 'needs-action': 0, running: 0, completed: 0 })
  const [inputRun, setInputRun] = useState<AccountCustomWorkflowRunSummary | null>(null)
  const [selectedRun, setSelectedRun] = useState<AccountCustomWorkflowRunSummary | null>(null)
  const [triggerGetRun, { data: runDetail, isFetching: runDetailLoading, isError: runDetailError }] = useLazyGetCustomWorkflowRunQuery()
  const [triggerGetWorkflow, { data: workflowDetail }] = useLazyGetCustomWorkflowQuery()

  const paramTab = searchParams.get(TAB_PARAM) as WorkflowTab | null
  const activeTab: WorkflowTab = paramTab && VALID_TABS.includes(paramTab) ? paramTab : 'needs-action'
  const setActiveTab = (tab: WorkflowTab) => {
    const next = new URLSearchParams(searchParams)
    next.set(TAB_PARAM, tab)
    setSearchParams(next, { replace: true })
  }

  const runsBaseOptions = { skip: !accountId, skipPollingIfUnfocused: true }
  const activeRunsOptions = { ...runsBaseOptions, pollingInterval: 10000 }
  const statusFor = (key: RunTabKey) => RUN_TABS.find((t) => t.key === key)!.status
  const needsAction = useListAccountCustomWorkflowRunsQuery(
    { accountId, status: statusFor('needs-action'), page: pages['needs-action'], size: PAGE_SIZE },
    activeRunsOptions,
  )
  const running = useListAccountCustomWorkflowRunsQuery(
    { accountId, status: statusFor('running'), excludeSubRuns: true, page: pages.running, size: PAGE_SIZE },
    activeRunsOptions,
  )
  const completed = useListAccountCustomWorkflowRunsQuery(
    { accountId, status: statusFor('completed'), excludeSubRuns: true, page: pages.completed, size: PAGE_SIZE },
    runsBaseOptions,
  )
  const byKey: Record<RunTabKey, typeof needsAction> = { 'needs-action': needsAction, running, completed }

  const totalPages: Record<RunTabKey, number> = {
    'needs-action': needsAction.data?.page.totalPages ?? 0,
    running: running.data?.page.totalPages ?? 0,
    completed: completed.data?.page.totalPages ?? 0,
  }
  const [prevTotalPages, setPrevTotalPages] = useState<Record<RunTabKey, number>>(totalPages)
  if (
    prevTotalPages['needs-action'] !== totalPages['needs-action'] ||
    prevTotalPages.running !== totalPages.running ||
    prevTotalPages.completed !== totalPages.completed
  ) {
    setPrevTotalPages(totalPages)
    setPages((prev) => clampPages(prev, totalPages))
  }

  const countFor = (q: typeof needsAction) => (q.isLoading ? undefined : (q.data?.page.totalElements ?? 0))
  const counts = {
    'needs-action': countFor(needsAction),
    running: countFor(running),
    completed: countFor(completed),
  }

  const runKey: RunTabKey | null = activeTab === 'recommended' ? null : (activeTab as RunTabKey)
  const runDef = runKey ? RUN_TABS.find((t) => t.key === runKey)! : null
  const runQuery = runKey ? byKey[runKey] : null

  const onViewResult = (run: AccountCustomWorkflowRunSummary) => {
    setSelectedRun(run)
    if (accountId) {
      void triggerGetRun({ accountId, workflowId: run.workflowId, runId: run.id })
      void triggerGetWorkflow({ accountId, workflowId: run.workflowId })
    }
  }

  const actionForTab = (key: RunTabKey): ((run: AccountCustomWorkflowRunSummary) => RunRowAction | undefined) | undefined => {
    if (key === 'needs-action') {
      return (run) => ({ label: 'Provide input', variant: 'primary', onClick: () => setInputRun(run) })
    }
    if (key === 'completed') {
      return (run) => ({ label: 'View result', variant: 'grayGhost', onClick: () => onViewResult(run) })
    }
    return undefined
  }

  return (
    <Container fluid className='p-4 d-flex flex-column zocks-gap-2'>
      <h3>Workflows</h3>
      <WorkflowsTabBar activeTab={activeTab} counts={counts} onSelect={setActiveTab} />
      <div className='pt-2'>
        {activeTab === 'recommended' ? (
          <RecommendedWorkflows accountId={accountId} active={activeTab === 'recommended'} />
        ) : runKey && runDef && runQuery ? (
          <RunsTab
            key={runKey}
            accountId={accountId}
            runs={runQuery.data?.data ?? []}
            isLoading={runQuery.isLoading}
            isError={runQuery.isError}
            page={pages[runKey]}
            totalPages={runQuery.data?.page.totalPages ?? 0}
            onPageChange={(page) => setPages((prev) => ({ ...prev, [runKey]: page }))}
            emptyMessage={runDef.emptyMessage}
            actionFor={actionForTab(runKey)}
            showSubRunBadge={runKey === 'needs-action'}
            groupChildRuns={runKey !== 'needs-action'}
          />
        ) : null}
      </div>

      {inputRun && accountId ? (
        <ProvideInputModal
          accountId={accountId}
          workflowId={inputRun.workflowId}
          runId={inputRun.id}
          title={inputRun.workflowName || 'Provide input'}
          show={!!inputRun}
          onHide={() => setInputRun(null)}
        />
      ) : null}

      <Modal
        size='xl'
        show={!!selectedRun}
        onHide={() => setSelectedRun(null)}
        bodyClassName='pb-4'
        header={<h5 className='mb-0'>{selectedRun?.workflowName || 'Workflow run'}</h5>}
        body={
          selectedRun ? (
            <>
              <div className='mb-3'>
                <WorkflowRunStatusBadge status={selectedRun.status} />
              </div>
              {selectedRun.context?.length ? <RunContextDetail context={selectedRun.context} accountId={accountId ?? ''} /> : null}
              {runDetailLoading ? (
                <div className='d-flex justify-content-center pt-2'>
                  <Spinner />
                </div>
              ) : null}
              {runDetailError ? (
                <Alert variant='warning' className='mb-0'>
                  Could not load the run output.
                </Alert>
              ) : null}
              {!runDetailLoading && !runDetailError && runDetail ? (
                <>
                  {runDetail.inputValues?.length || runDetail.files?.length ? (
                    <RunInputSummary
                      inputValues={runDetail.inputValues ?? []}
                      files={runDetail.files ?? []}
                      accountId={accountId}
                      workflowId={selectedRun.workflowId}
                      runId={selectedRun.id}
                    />
                  ) : null}
                  {runDetail.error ? (
                    <Alert variant='danger' className='mb-0'>
                      {runDetail.error}
                    </Alert>
                  ) : (
                    renderWorkflowOutputs(
                      runDetail.outputs,
                      hintsFromSchema(workflowDetail?.id === selectedRun.workflowId ? workflowDetail.outputSchema : undefined),
                      { accountId, workflowId: selectedRun.workflowId, runId: selectedRun.id },
                    )
                  )}
                </>
              ) : null}
            </>
          ) : null
        }
      />
    </Container>
  )
}

export default WorkflowsLanding
